<template>
  <v-card data-testid="grid_sequence_edit">
    <v-card-title class="pa-0">
      <v-toolbar flat>
        <v-btn icon @click="$emit('close')"><v-icon>close</v-icon></v-btn>
        <v-toolbar-title>{{ grid_sequence.title || $t("grid_sequence_edit.new_sequence") }}</v-toolbar-title>
        <v-spacer />
        <v-btn small color="primary" class="mr-2" data-testid="grid_sequence_editor_toggle" @click="toggle_editor">
          {{ $t(editor_mode === "form" ? "grid_sequence_edit.use_visual_editor" : "grid_sequence_edit.use_form_editor") }}
        </v-btn>
        <v-btn
          v-if="!is_fullscreen"
          small
          outlined
          data-testid="grid_sequence_edit_fullscreen"
          @click="$emit('fullscreen', true)"
        >
          <v-icon left>fas fa-expand-arrows-alt</v-icon>{{ $t("common.fullscreen") }}
        </v-btn>
        <v-btn
          v-else
          small
          outlined
          data-testid="grid_sequence_edit_exit_fullscreen"
          @click="$emit('fullscreen', false)"
        >
          <v-icon left>fas fa-compress-arrows-alt</v-icon>{{ $t("common.exit_fullscreen") }}
        </v-btn>
      </v-toolbar>
    </v-card-title>
    <v-divider />

    <v-card-text class="pt-6">
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            ref="title"
            v-model="grid_sequence.title"
            :label="$t('common.title')"
            required
            dense
            :readonly="readonly"
            data-testid="grid_sequence_edit_title"
            @input="unsaved_data = true"
          />
        </v-col>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="grid_sequence.description"
            :label="$t('common.description')"
            dense
            :readonly="readonly"
            data-testid="grid_sequence_edit_description"
            @input="unsaved_data = true"
          />
        </v-col>
      </v-row>

      <GridSequenceStepList
        v-show="editor_mode === 'form'"
        v-model="grid_sequence.sequence"
        :readonly="readonly"
        :additional_video_exercises="additional_video_exercises"
        @add-step="add_step"
        @add-exercise="add_exercise"
        @edit-exercise="edit_exercise"
        @input="unsaved_data = true"
        @deleted="unsaved_data = true"
        @duplicated="unsaved_data = true"
        @dragged="unsaved_data = true"
      />
      <GridSequenceDrawflowEdit
        v-show="editor_mode === 'visual'"
        v-model="grid_sequence.sequence"
        :visible="editor_mode === 'visual'"
        :readonly="readonly"
        :additional_video_exercises="additional_video_exercises"
        @add-step="add_step"
        @add-exercise="add_exercise"
        @edit-exercise="edit_exercise"
        @input="unsaved_data = true"
        @deleted="unsaved_data = true"
        @duplicated="unsaved_data = true"
        @dragged="unsaved_data = true"
      />

      <CommunitySharing
        v-if="!existing_sequence && app_config.community_sharing && !readonly && !grid_sequence.share_source"
        v-model="grid_sequence.shared"
        :hint_disabled="$t('grid_sequence_edit.sharing_hint_disabled')"
        :hint_enabled="$t('grid_sequence_edit.sharing_hint_enabled')"
        :item_title="grid_sequence.title"
      />
    </v-card-text>

    <v-card-actions v-if="!readonly">
      <v-alert v-if="error_message" dense outlined type="error" class="mb-0">{{ error_message }}</v-alert>
      <span v-else-if="unsaved_data" class="ml-4">{{ $t("grid_sequence_edit.edited_unsaved_changes") }}</span>
      <v-spacer />
      <v-btn text @click="discard" data-testid="grid_sequence_edit_discard">{{ $t("regimen_edit.discard_changes") }}</v-btn>
      <v-btn
        v-if="grid_sequence_id && !existing_sequence"
        color="primary lighten-1"
        :disabled="in_flight || grid_sequence.sequence.length === 0"
        data-testid="grid_sequence_edit_save_update_associated_patients"
        @click="submit(true)"
      >
        <div>
          {{ $t("common.save") }}
          <div style="font-size: x-small; text-transform: none">{{ $t("grid_sequence_edit.update_associated_patients") }}</div>
        </div>
      </v-btn>
      <v-btn
        color="primary"
        :disabled="in_flight || grid_sequence.sequence.length === 0"
        data-testid="grid_sequence_edit_save"
        @click="submit(false)"
      >
        <div>
          {{ $t("common.save") }}
          <div v-if="grid_sequence_id && !existing_sequence" style="font-size: x-small; text-transform: none">
            {{ $t("grid_sequence_edit.dont_update_patients") }}
          </div>
        </div>
      </v-btn>
    </v-card-actions>
    <v-progress-linear v-if="in_flight" indeterminate />

    <v-dialog v-model="exercise_dialog" max-width="1200">
      <RegimenExerciseMetaEdit
        v-if="exercise_dialog"
        v-model="current_exercise"
        context="grid_sequence"
        :allowed_exercise_types="current_allowed_exercise_types"
        @save="save_step"
      />
    </v-dialog>

    <v-dialog v-model="sharing_dependencies_dialog" max-width="800">
      <v-card>
        <v-card-title>{{ $t("grid_sequence_edit.sharing_dependencies_title") }}</v-card-title>
        <v-card-text>
          {{ $t("grid_sequence_edit.sharing_dependencies_body") }}
          <ul class="mt-3">
            <li v-for="video in sharing_dependencies" :key="video">{{ video }}</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="
              grid_sequence.shared = null;
              sharing_dependencies_dialog = false;
              perform_submit();
            "
          >
            {{ $t("common.save_without_sharing") }}
          </v-btn>
          <v-btn
            color="primary"
            @click="
              sharing_dependencies_dialog = false;
              perform_submit();
            "
          >
            {{ $t("common.confirm_additional_sharing") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import axios from "axios";
import uuid from "uuid";
import { mapState } from "vuex";
import GridSequenceDrawflowEdit from "./GridSequenceDrawflowEdit.vue";
import GridSequenceStepList from "./GridSequenceStepList.vue";
import CommunitySharing from "./CommunitySharing.vue";
import {
  appendExercise,
  buildConditionFields,
  defaultStepCompletionCondition,
  isNeverCompletionCondition,
  isReportableExercise,
  migrateStepExercise,
  neverStepCompletionCondition,
  replaceStepExercise,
  validateCompletionCondition,
} from "../classes/grid_sequence";

export default {
  components: {
    GridSequenceDrawflowEdit,
    GridSequenceStepList,
    RegimenExerciseMetaEdit: () => import("./RegimenExerciseMetaEdit.vue"),
    CommunitySharing,
  },
  props: {
    grid_sequence_id: { type: String, default: "" },
    existing_sequence: { type: Object, default: null },
    assignment: { type: Object, default: null },
    clinic_id: { type: String, required: true },
    readonly: { type: Boolean, default: false },
    additional_video_exercises: { type: Object, default: () => ({}) },
    is_fullscreen: { type: Boolean, default: false },
  },
  data() {
    return {
      grid_sequence: this.blank_sequence(),
      current_exercise: this.blank_exercise(),
      current_step_index: null,
      current_exercise_id: null,
      exercise_dialog: false,
      in_flight: false,
      unsaved_data: false,
      error_message: "",
      sharing_dependencies: [],
      sharing_dependencies_dialog: false,
      update_regimens_on_submit: false,
      editor_mode: "visual",
    };
  },
  computed: {
    ...mapState(["app_config", "exercises", "video_exercises"]),
    current_allowed_exercise_types() {
      const types = ["interactive", "video", "custom", "question", "questionnaire", "email"];
      return this.current_step_index === null ? types.filter((type) => type !== "email") : types;
    },
    all_video_exercises() {
      return { ...(this.additional_video_exercises || {}), ...(this.video_exercises || {}) };
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    blank_sequence() {
      return {
        clinic_id: this.clinic_id,
        title: "",
        description: "",
        instructions: "",
        sequence: [],
        shared: null,
        share_source: null,
      };
    },
    blank_exercise() {
      return {
        uuid: uuid.v4(),
        exercise_type: "",
        exercise_id: null,
        title: null,
        instructions: "",
        config: {},
        level: 0,
        config_version: 0,
      };
    },
    load() {
      if (this.existing_sequence) {
        this.grid_sequence = JSON.parse(JSON.stringify(this.existing_sequence));
        this.unsaved_data = false;
        return;
      }
      if (!this.grid_sequence_id) return;
      this.in_flight = true;
      axios.get(`/api/grid_sequence/${this.grid_sequence_id}`).then((response) => {
        this.in_flight = false;
        if (response.data.status === "success") {
          this.grid_sequence = response.data.data;
          this.unsaved_data = false;
        } else {
          messageBus.$emit("api-error", response.data);
          this.$emit("close");
        }
      });
    },
    discard() {
      if (!confirm(this.$t("grid_sequence_edit.discard_confirm"))) return;
      this.grid_sequence = this.blank_sequence();
      this.load();
      this.unsaved_data = false;
    },
    add_step() {
      this.current_step_index = null;
      this.current_exercise_id = null;
      this.current_exercise = this.blank_exercise();
      this.exercise_dialog = true;
    },
    add_exercise(stepIndex) {
      this.current_step_index = stepIndex;
      this.current_exercise_id = null;
      this.current_exercise = this.blank_exercise();
      this.exercise_dialog = true;
    },
    edit_exercise(stepIndex, exerciseId) {
      this.current_step_index = stepIndex;
      this.current_exercise_id = exerciseId;
      const exercise = this.grid_sequence.sequence[stepIndex].exercises.find((candidate) => candidate.uuid === exerciseId);
      this.current_exercise = JSON.parse(JSON.stringify(exercise));
      this.exercise_dialog = true;
    },
    empty_completion_condition(exercise) {
      return this.editor_mode === "visual"
        ? neverStepCompletionCondition()
        : defaultStepCompletionCondition(exercise);
    },
    condition_fields(step) {
      return buildConditionFields(
        step,
        this.exercises || {},
        this.all_video_exercises || {},
        this.$i18n.lang(),
        this.$t.bind(this),
      );
    },
    toggle_editor() {
      this.editor_mode = this.editor_mode === "form" ? "visual" : "form";
    },
    save_step() {
      const exercise = JSON.parse(JSON.stringify(this.current_exercise));
      if (this.current_step_index === null) {
        this.grid_sequence.sequence.push({
          id: uuid.v4(),
          title: "",
          exercises: [exercise],
          completion_conditions: [this.empty_completion_condition(exercise)],
        });
      } else {
        const existing = this.grid_sequence.sequence[this.current_step_index];
        const previous = this.current_exercise_id
          ? existing.exercises.find((candidate) => candidate.uuid === this.current_exercise_id)
          : null;
        let step;
        if (previous && previous.uuid !== exercise.uuid) {
          step = replaceStepExercise(
            existing,
            previous.uuid,
            exercise,
            this.empty_completion_condition(exercise),
          );
        } else if (!this.current_exercise_id) {
          step = appendExercise(existing, exercise);
        } else {
          const updated = {
            ...existing,
            exercises: existing.exercises.map((candidate) =>
              candidate.uuid === exercise.uuid ? exercise : candidate),
          };
          step = migrateStepExercise(
            existing,
            exercise,
            this.condition_fields(existing),
            this.condition_fields(updated),
            this.empty_completion_condition(exercise),
          );
        }
        this.$set(this.grid_sequence.sequence, this.current_step_index, step);
      }
      this.exercise_dialog = false;
      this.unsaved_data = true;
    },
    validate() {
      if (!this.grid_sequence.title.trim()) {
        this.error_message = this.$t("grid_sequence_edit.title_required");
        this.$refs.title.focus();
        return false;
      }
      if (this.grid_sequence.sequence.length === 0) {
        this.error_message = this.$t("grid_sequence_edit.add_at_least_one_exercise");
        return false;
      }
      if (this.grid_sequence.sequence.some((step) => step.exercises.length === 0)) {
        this.error_message = this.$t("grid_sequence_edit.add_at_least_one_exercise_per_step");
        return false;
      }
      if (this.grid_sequence.sequence.some((step) => !step.exercises.some(isReportableExercise))) {
        this.error_message = this.$t("grid_sequence_edit.add_reportable_exercise_per_step");
        return false;
      }
      const stepIds = new Set(this.grid_sequence.sequence.map((step) => step.id));
      if (this.assignment
        && this.assignment.active_step !== "complete"
        && !stepIds.has(this.assignment.active_step)) {
        this.error_message = this.$t("regimen_grid_sequence_edit.active_step_missing");
        return false;
      }
      if (this.grid_sequence.sequence.some((step) =>
        step.completion_conditions.some((completion) =>
          completion.target_step === null && !isNeverCompletionCondition(completion.condition)))) {
        this.error_message = this.$t("grid_sequence_edit.target_step_required");
        return false;
      }
      if (this.grid_sequence.sequence.some((step) =>
        !step.completion_conditions.length
        || step.completion_conditions.some((completion) =>
          !validateCompletionCondition(completion.condition)
          || (isNeverCompletionCondition(completion.condition) && completion.target_step !== null)
          || completion.target_step === step.id
          || (completion.target_step !== null
            && completion.target_step !== "complete"
            && !stepIds.has(completion.target_step))))) {
        this.error_message = this.$t("grid_sequence_edit.complete_all_conditions");
        return false;
      }
      if (this.grid_sequence.sequence.some((step) =>
        step.completion_conditions.some((completion) =>
          !this.condition_references_step_exercises(completion.condition, step.exercises)))) {
        this.error_message = this.$t("grid_sequence_edit.complete_all_conditions");
        return false;
      }
      this.error_message = "";
      return true;
    },
    condition_references_step_exercises(condition, exercises) {
      if (condition.type === "always") return true;
      if (condition.type === "group") {
        return condition.conditions.every((child) => this.condition_references_step_exercises(child, exercises));
      }
      if (condition.type === "exercise_completed_count") {
        return exercises.some((exercise) => exercise.uuid === condition.exercise_id);
      }
      if (condition.type === "step_live_days") return true;
      return exercises.some((exercise) => exercise.uuid === condition.field.split(".")[0]);
    },
    async sharing_dependencies_to_confirm() {
      if (!this.grid_sequence.shared) return [];
      let dependency_titles = [];
      if (this.grid_sequence.id) {
        const response = await axios.get(`/api/shared/dependencies/grid_sequence/${this.grid_sequence.id}`);
        if (response.data.status === "success") {
          dependency_titles = response.data.data.filter((dependency) => dependency.needs_share).map((dependency) => dependency.title);
        } else {
          messageBus.$emit("api-error", response.data);
        }
      }
      const ids = [...new Set(this.grid_sequence.sequence
        .flatMap((step) => step.exercises)
        .filter((exercise) => exercise.exercise_type === "video/clinic")
        .map((exercise) => exercise.exercise_id))];
      const responses = await Promise.all(ids.map((id) => axios.get(`/api/video_exercise/${id}`)));
      const private_videos = responses
        .filter((response) => response.data.status === "success" && !response.data.data.shared)
        .map((response) => response.data.data.title);
      return [...new Set([...dependency_titles, ...private_videos])];
    },
    async submit(updateRegimens = false) {
      if (!this.validate()) return;
      this.update_regimens_on_submit = updateRegimens;
      if (this.existing_sequence) {
        this.unsaved_data = false;
        this.$emit("grid-sequence-updated", JSON.parse(JSON.stringify(this.grid_sequence)));
        this.$emit("close");
        return;
      }
      this.sharing_dependencies = await this.sharing_dependencies_to_confirm();
      if (this.sharing_dependencies.length) {
        this.sharing_dependencies_dialog = true;
        return;
      }
      this.perform_submit();
    },
    perform_submit() {
      this.in_flight = true;
      const request = this.grid_sequence.id
        ? axios.put(`/api/grid_sequence/${this.grid_sequence.id}?update_regimens=${this.update_regimens_on_submit}`, this.grid_sequence)
        : axios.post("/api/grid_sequence", this.grid_sequence);
      request.then((response) => {
        this.in_flight = false;
        if (response.data.status !== "success") {
          messageBus.$emit("api-error", response.data);
          return;
        }
        if (!this.grid_sequence.id) {
          this.grid_sequence.id = response.data.data;
          this.$emit("grid-sequence-created", this.grid_sequence);
        } else {
          this.$emit("grid-sequence-updated", this.grid_sequence);
        }
        this.unsaved_data = false;
        messageBus.$emit("success", this.$t("grid_sequence_edit.sequence_saved"));
        this.$emit("close");
      }).catch(() => {
        this.in_flight = false;
      });
    },
  },
};
</script>
